import { useState } from 'react';
import styled from 'styled-components';
import { Button, Dropdown } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import {
  ActiveFilters,
  ButtonFilter,
  StyledContent,
} from '../../../components/ModelInstanceFilter';
import { useToggleVisibility } from '../../../hooks';
import { FilterGroupProvider } from './FilterGroupContext';
import FilterGroupNav from './FilterGroupNav';
import FilterGroupContent from './FilterGroupContent';
import type { IFilterProps } from './Filter';

export interface IFilterGroupProps {
  key: string;
  type: 'group';
  name: string;
  defaultVisible: boolean;
  visible?: boolean;
  basePath: string; // e.g. "sudCases[].variations[]"
  filters: { [childKey: string]: IFilterProps };
  order?: number;
}

type Selections = { [childKey: string]: string[] };

interface IProps extends IFilterGroupProps {
  default?: Selections;
  context?: Record<string, any>;
  onApply: (selections: Selections) => void;
  onRemove?: () => void;
}

const FilterGroup: React.FC<IProps> = ({
  name,
  filters,
  default: initialSelections,
  onApply,
  onRemove,
  context,
}) => {
  const { isVisible, show, hide } = useToggleVisibility();
  const [committed, setCommitted] = useState<Selections>(
    initialSelections || {}
  );

  const totalSelected = Object.values(committed).reduce(
    (sum, values) => sum + (values?.length || 0),
    0
  );

  const handleApply = (selections: Selections) => {
    setCommitted(selections);
    onApply(selections);
    hide();
  };

  const handleClear = () => {
    setCommitted({});
    onApply({});
    hide();
  };

  return (
    <Dropdown
      trigger={['click']}
      open={isVisible}
      onOpenChange={visible => (visible ? show() : hide())}
      placement="bottomLeft"
      popupRender={() => (
        <StyledContent $minWidth={360} onMouseDown={e => e.stopPropagation()}>
          <FilterGroupProvider
            childFilters={filters}
            isVisible={isVisible}
            context={context}
            initialSelections={committed}
            onApply={handleApply}
            onClear={handleClear}
          >
            <PanesRow>
              <FilterGroupNav />
              <FilterGroupContent />
            </PanesRow>
          </FilterGroupProvider>
        </StyledContent>
      )}
    >
      <ButtonFilter>
        <span>{name}</span>
        {totalSelected > 0 && (
          <ActiveFilters>
            <span>{totalSelected}</span>
          </ActiveFilters>
        )}
        {onRemove && (
          <RemoveButton
            size="small"
            type="text"
            icon={<CloseOutlined />}
            onClick={onRemove}
          />
        )}
      </ButtonFilter>
    </Dropdown>
  );
};

export default FilterGroup;

const PanesRow = styled.div`
  display: flex;
  gap: 8px;
`;

const RemoveButton = styled(Button)`
  padding: 4px;
  height: auto;
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;
